import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Flame, Wheat, Beef, Cookie, ArrowLeft, Info, Sparkles, Send, Utensils } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useEffect, useState } from "react";
import { useCachedImage } from "@/hooks/use-cached-image";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";


// Separate component for ingredient card to reduce complexity
const IngredientCard = ({ ingredient }: { ingredient: any }) => (
  <Card key={ingredient.id}>
    <CardContent className="flex flex-col md:flex-row md:items-center justify-between p-4">
      <div>
        <div className="flex items-center gap-2">
          <p className="font-medium">{ingredient.ingredient_name}</p>
          {ingredient.quantity_in_grams && (
            <p className="text-sm text-muted-foreground">
              ({Math.round(ingredient.quantity_in_grams)}g)
            </p>
          )}
        </div>
        {ingredient.food_group && (
          <p className="text-sm text-muted-foreground">
            <span>{ingredient.nutrition_fact_name}</span>
            <span> • </span>
            <span>{ingredient.food_group}</span>
          </p>
        )}
        <div className="grid grid-cols-4 gap-2 my-2 md:hidden">
          <MacroNutrient
            icon={<Flame className="h-4 w-4 text-orange-500" />}
            value={ingredient.calories || 0}
            unit=""
            bgColor="bg-orange-50"
          />
          <MacroNutrient
            icon={<Wheat className="h-4 w-4 text-yellow-600" />}
            value={ingredient.carbs_grams || 0}
            unit="g"
            bgColor="bg-yellow-50"
          />
          <MacroNutrient
            icon={<Beef className="h-4 w-4 text-red-500" />}
            value={ingredient.protein_grams || 0}
            unit="g"
            bgColor="bg-red-50"
          />
          <MacroNutrient
            icon={<Cookie className="h-4 w-4 text-blue-500" />}
            value={ingredient.fat_grams || 0}
            unit="g"
            bgColor="bg-blue-50"
          />
        </div>
        <div className="inline-flex items-center gap-1 mt-1 text-xs text-muted-foreground bg-secondary/50 px-2 py-1 rounded">
          <Info className="h-3 w-3" />
          <span>Per 100g:</span>
          <span>{ingredient.base_calories} cal</span>
          <span>•</span>
          <span>{ingredient.base_carbs}g carbs</span>
          <span>•</span>
          <span>{ingredient.base_protein}g protein</span>
          <span>•</span>
          <span>{ingredient.base_fat}g fat</span>
        </div>
      </div>
      <div className="hidden md:grid grid-cols-4 gap-2">
        <MacroNutrient
          icon={<Flame className="h-4 w-4 text-orange-500" />}
          value={ingredient.calories || 0}
          unit=""
          bgColor="bg-orange-50"
        />
        <MacroNutrient
          icon={<Wheat className="h-4 w-4 text-yellow-600" />}
          value={ingredient.carbs_grams || 0}
          unit="g"
          bgColor="bg-yellow-50"
        />
        <MacroNutrient
          icon={<Beef className="h-4 w-4 text-red-500" />}
          value={ingredient.protein_grams || 0}
          unit="g"
          bgColor="bg-red-50"
        />
        <MacroNutrient
          icon={<Cookie className="h-4 w-4 text-blue-500" />}
          value={ingredient.fat_grams || 0}
          unit="g"
          bgColor="bg-blue-50"
        />
      </div>
    </CardContent>
  </Card>
);

// Separate component for macro nutrient display
const MacroNutrient = ({ 
  icon, 
  value, 
  unit, 
  bgColor, 
  label = null // Make label optional with a default value
}: { 
  icon: React.ReactNode; 
  value: number; 
  unit: string; 
  bgColor: string; 
  label?: string | null; // Change to optional
}) => (
  <div className={`flex items-center gap-2 ${bgColor} p-2 rounded-lg`}>
    {icon}
    <div>
      <p className="font-medium">{Math.round(value)}{unit}</p>
      {label && <p className="text-sm text-muted-foreground">{label}</p>}
    </div>
  </div>
);

const EntryDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showTextBox, setShowTextBox] = useState(false);
  
  useEffect(() => {
    if (!id) {
      navigate('/', { replace: true });
    }
  }, [id, navigate]);

  const { data: entry, isLoading, error } = useQuery({
    queryKey: ['entry', id],
    queryFn: async () => {
      if (!id) throw new Error('No entry ID provided');

      const { data: entry, error: entryError } = await supabase
        .from('v_food_entries_aggregated')
        .select('*')
        .eq('entry_id', id)
        .maybeSingle();

      if (entryError) throw entryError;
      if (!entry) throw new Error('Entry not found');

      const { data: ingredients, error: ingredientsError } = await supabase
        .from('v_food_entry_ingredients_detailed')
        .select('*')
        .eq('food_entry_id', id)
        .order('ingredient_name');

      if (ingredientsError) throw ingredientsError;

      return {
        ...entry,
        ingredients,
      };
    },
    enabled: !!id,
  });

  const { imageUrl, isLoading: imageLoading } = useCachedImage(
    entry?.hero_image_path || null
  );

  if (!id) {
    return null;
  }

  if (isLoading) {
    return <div className="text-center mt-8">Loading...</div>;
  }

  if (error) {
    return <div className="text-center mt-8 text-red-500">Error loading entry details</div>;
  }

  if (!entry) {
    return <div className="text-center mt-8">Entry not found</div>;
  }

  const renderImage = () => {
    if (imageLoading) {
      return <div className="animate-pulse bg-gray-200 w-full h-96 rounded-lg" />;
    }

    if (!entry.hero_image_path || !imageUrl) {
      return (
        <div className="bg-gray-100 w-full h-96 rounded-lg flex items-center justify-center text-gray-500">
          <Utensils className="h-16 w-16" />
        </div>
      );
    }

    return (
      <img
        src={imageUrl}
        alt={entry.entry_name || 'Food Entry'}
        className="w-full h-96 object-cover rounded-lg"
      />
    );
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Button 
        variant="ghost" 
        onClick={() => navigate(-1)}
        className="mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </Button>
      <div className="space-y-8">
        {renderImage()}

        <div>
          <h1 className="text-3xl font-bold mb-2">{entry.entry_name || 'Unnamed Entry'}</h1>
          {entry.notes && (
            <p className="text-muted-foreground mt-2">{entry.notes}</p>
          )}
          <p className="text-muted-foreground">
            {format(new Date(entry.consumed_at), 'PPpp')}
          </p>
        </div>

        {(entry.total_calories || entry.total_carbs || entry.total_protein || entry.total_fat) ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MacroNutrient
              icon={<Flame className="h-6 w-6 text-orange-500" />}
              value={entry.total_calories || 0}
              unit=""
              bgColor="bg-orange-50"
              label="calories"
            />
            <MacroNutrient
              icon={<Wheat className="h-6 w-6 text-yellow-600" />}
              value={entry.total_carbs || 0}
              unit="g"
              bgColor="bg-yellow-50"
              label="carbs"
            />
            <MacroNutrient
              icon={<Beef className="h-6 w-6 text-red-500" />}
              value={entry.total_protein || 0}
              unit="g"
              bgColor="bg-red-50"
              label="protein"
            />
            <MacroNutrient
              icon={<Cookie className="h-6 w-6 text-blue-500" />}
              value={entry.total_fat || 0}
              unit="g"
              bgColor="bg-blue-50"
              label="fat"
            />
          </div>
        ) : (
          <Card>
            <CardContent className="p-4">
              <p className="text-center text-muted-foreground">No nutrition data available</p>
            </CardContent>
          </Card>
        )}

        <div>
          {!showTextBox && (
            <Button 
              onClick={() => {
                setShowTextBox(true);
                // Focus will happen after render
                setTimeout(() => {
                  document.querySelector('textarea')?.focus();
                }, 0);
              }}          
              className="w-full"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Fix
            </Button>
          )}
          {showTextBox && (
            <div className="grid w-full gap-2 mt-2">
              <Textarea 
                placeholder="(COMING SOON!) Describe what you want to change. Describe the ingredients that you want to add or remove or an estimate of how much of an ingredient you used. Try things like this was tofu not chicken or I used a handful or strawberries."
                className="min-h-[120px]"
              />
              <Button
                onClick={() => {
                  setShowTextBox(false);
                  toast.info("Fix with AI coming soon!");
                }}   
                className="w-full mt-2"
              >
                <Send className="h-4 w-4 mr-2" />
                Submit
              </Button>
            </div>
          )}
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-4">Ingredients</h2>
          {entry.ingredients && entry.ingredients.length > 0 ? (
            <div className="space-y-3">
              {entry.ingredients.map((ingredient: any) => (
                <IngredientCard key={ingredient.id} ingredient={ingredient} />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-6">
                <div className="text-center text-muted-foreground">
                  <p className="mb-2">No ingredients found</p>
                  {entry.analysis_status !== 'completed' && (
                    <p className="text-sm">Analysis in progress...</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default EntryDetails;